import React, { useState } from "react";
import { useI18n } from "../../i18n";
import NewUserModal from "./NewUserModal";
import EditUserModal from "./EditUserModal";
import { useGetAllUsers, useDeleteUser } from "./hooks";

interface User {
  uuid: string;
  username: string;
  email: string;
  userRole: string;
  accountType: string;
  contentAccess?: string[];
  partnerCompany: string;
  created_at: string;
  updatedAt: string;
  company?: {
    uuid: string;
    companyName: string;
    companyVatId: string;
    partnershipLevel: string;
  };
}

const Users: React.FC = () => {
  const { t, code } = useI18n();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [newUserModalOpen, setNewUserModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Use admin API hooks
  const { data: usersResponse, isLoading, error, refetch } = useGetAllUsers();
  const deleteUserMutation = useDeleteUser();

  const users = usersResponse?.data || [];

  // Debug: Log the users data to see what we're getting
  console.log("Users data:", users);

  const handleUserSuccess = () => {
    setSnackbar(t("users.toast.created"));
    setTimeout(() => setSnackbar(null), 3000);
    refetch(); // Refresh user list
  };

  const handleEditSuccess = () => {
    setSnackbar(t("users.toast.updated"));
    setTimeout(() => setSnackbar(null), 3000);
    refetch(); // Refresh user list
  };

  const handleDeleteUser = async (uuid: string) => {
    if (window.confirm(t("users.confirmDelete"))) {
      try {
        await deleteUserMutation.mutateAsync(uuid);
        setSnackbar(t("users.toast.deleted"));
        setTimeout(() => setSnackbar(null), 3000);
      } catch (error) {
        console.error("Failed to delete user:", error);
      }
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(
    (user: User) =>
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.userRole?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.company?.companyName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedUsers(filteredUsers.map((user: User) => user.uuid));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleUserSelect = (userUuid: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers((prev) => [...prev, userUuid]);
    } else {
      setSelectedUsers((prev) => prev.filter((id) => id !== userUuid));
      setSelectAll(false);
    }
  };

  const getRoleBadge = (user: User) => {
    const baseClasses = "px-2.5 py-1 text-xs font-semibold rounded-full";

    // For admin users, show admin role
    if (user.accountType === "admin") {
      return `${baseClasses} text-purple-800 bg-purple-100`;
    }

    if (user.accountType === "marketer") {
      return `${baseClasses} text-amber-800 bg-amber-100`;
    }

    // For partner users, show user role
    switch (user.userRole) {
      case "superuser":
        return `${baseClasses} text-blue-800 bg-blue-100`;
      case "user":
        return `${baseClasses} text-green-800 bg-green-100`;
      default:
        return `${baseClasses} text-gray-800 bg-gray-100`;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString(code, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col flex-1 min-h-screen bg-gray-50 w-full h-full p-0 m-0">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">{t("users.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col flex-1 min-h-screen bg-gray-50 w-full h-full p-0 m-0">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <i className="fa-solid fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
            <p className="text-red-600 mb-2">{t("users.loadFailed")}</p>
            <p className="text-gray-500 text-sm">
              {t("users.refreshHint")}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t("common.retry")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-screen bg-gray-50 w-full h-full p-0 m-0">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:h-20 px-4 sm:px-8 py-4 sm:py-0 bg-white border-b border-gray-200 w-full">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{t("users.title")}</h2>
          <p className="text-xs sm:text-sm text-gray-500">
            {t("users.subtitle")}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setNewUserModalOpen(true)}
            className="w-full sm:w-auto justify-center px-5 py-2.5 flex items-center bg-[#111827] text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium shadow-sm"
          >
            <i className="fa-solid fa-user-plus mr-2"></i> {t("users.addUser")}
          </button>
        </div>
      </header>

      <main className="flex-1 p-4 sm:p-8 w-full h-full flex flex-col">
        <div className="flex-1 flex flex-col">
          {/* Users Table Container */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Table Controls */}
            <div className="p-4 border-b border-gray-200 bg-gray-50/50">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div className="flex items-center space-x-4">
                  <div className="relative w-full sm:w-72">
                    <input
                      type="text"
                      placeholder={t("users.searchPlaceholder")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-gray-800 transition"
                    />
                    <i className="fa-solid fa-search text-gray-400 absolute left-4 top-1/2 -translate-y-1/2"></i>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button className="px-4 py-2 flex items-center bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium">
                    <i className="fa-solid fa-filter mr-2"></i> {t("common.filter")}
                  </button>
                  <button className="px-4 py-2 flex items-center bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium">
                    <i className="fa-solid fa-file-export mr-2"></i> {t("common.export")}
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            {filteredUsers.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <i className="fa-solid fa-users text-gray-300 text-6xl mb-6"></i>
                  <p className="text-xl font-medium text-gray-600 mb-3">
                    {searchTerm ? t("users.empty.noResultsTitle") : t("users.empty.emptyTitle")}
                  </p>
                  <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
                    {searchTerm
                      ? t("users.empty.noResultsBody")
                      : t("users.empty.emptyBody")}
                  </p>
                  {!searchTerm && (
                    <button
                      onClick={() => setNewUserModalOpen(true)}
                      className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                      <i className="fa-solid fa-plus mr-2"></i>
                      {t("users.addFirstUser")}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-600">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th
                        scope="col"
                        className="p-4 font-semibold uppercase tracking-wider"
                      >
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="rounded border-gray-300 text-gray-800 focus:ring-gray-800"
                        />
                      </th>
                      <th
                        scope="col"
                        className="p-4 font-semibold uppercase tracking-wider"
                      >
                        {t("users.table.user")}
                      </th>
                      <th
                        scope="col"
                        className="p-4 font-semibold uppercase tracking-wider"
                      >
                        {t("users.table.role")}
                      </th>
                      <th
                        scope="col"
                        className="p-4 font-semibold uppercase tracking-wider"
                      >
                        {t("users.table.company")}
                      </th>
                      <th
                        scope="col"
                        className="p-4 font-semibold uppercase tracking-wider"
                      >
                        {t("users.table.created")}
                      </th>
                      <th
                        scope="col"
                        className="p-4 font-semibold uppercase tracking-wider"
                      >
                        {t("users.table.updated")}
                      </th>
                      <th
                        scope="col"
                        className="p-4 font-semibold uppercase tracking-wider text-right"
                      >
                        {t("users.table.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredUsers.map((user: User) => (
                      <tr key={user.uuid} className="hover:bg-gray-50/50">
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.uuid)}
                            onChange={(e) =>
                              handleUserSelect(user.uuid, e.target.checked)
                            }
                            className="rounded border-gray-300 text-gray-800 focus:ring-gray-800"
                          />
                        </td>
                        <td className="p-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                              <i className="fa-solid fa-user text-gray-500 text-sm"></i>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                {user.username || t("users.unknown")}
                              </div>
                              <div className="text-gray-500 text-xs">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 whitespace-nowrap">
                          <span className={getRoleBadge(user)}>
                            {user.accountType === "admin"
                              ? t("users.accountTypes.admin")
                              : user.accountType === "marketer"
                              ? t("users.accountTypes.marketer")
                              : user.userRole}
                          </span>
                        </td>
                        <td className="p-4 whitespace-nowrap">
                          {user.accountType === "partner" ? (
                            user.company ? (
                              <div>
                                <div className="font-medium text-gray-900">
                                  {user.company.companyName}
                                </div>
                                <div className="text-gray-500 text-xs">
                                  {user.company.partnershipLevel}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">
                                {t("users.noCompanyAssigned")}
                              </span>
                            )
                          ) : user.accountType === "marketer" ? (
                            <div className="flex flex-wrap gap-1 max-w-xs">
                              {(user.contentAccess ?? []).length === 0 ? (
                                <span className="text-gray-400 italic">
                                  {t("users.noSectionsAssigned")}
                                </span>
                              ) : (
                                (user.contentAccess ?? []).map((key) => (
                                  <span
                                    key={key}
                                    className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs"
                                  >
                                    {t(`contentSections.${key}`)}
                                  </span>
                                ))
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                        <td className="p-4 whitespace-nowrap text-gray-500">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="p-4 whitespace-nowrap text-gray-500">
                          {formatDate(user.updatedAt)}
                        </td>
                        <td className="p-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => setEditUser(user)}
                              aria-label={t("common.edit")}
                              className="text-blue-600 hover:text-blue-800 p-2"
                            >
                              <i className="fa-solid fa-edit text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.uuid)}
                              aria-label={t("common.delete")}
                              className="text-red-600 hover:text-red-800 p-2"
                            >
                              <i className="fa-solid fa-trash text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {filteredUsers.length > 0 && (
              <div className="p-4 flex flex-col sm:flex-row items-center gap-3 sm:justify-between text-xs sm:text-sm border-t border-gray-200">
                <p className="text-gray-600">
                  {t("common.showingRange", {
                    from: 1,
                    to: filteredUsers.length,
                    total: users.length,
                    entity: t("users.entity"),
                  })}
                  {searchTerm && (
                    <span className="ml-2 text-gray-400">
                      {t("users.filteredFrom", { total: users.length })}
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <NewUserModal
        open={newUserModalOpen}
        onClose={() => setNewUserModalOpen(false)}
        onSuccess={handleUserSuccess}
      />

      <EditUserModal
        open={!!editUser}
        user={editUser}
        onClose={() => setEditUser(null)}
        onSuccess={handleEditSuccess}
      />

      {snackbar && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 font-medium animate-fade-in">
          {snackbar}
        </div>
      )}
    </div>
  );
};

export default Users;
